import React, { Component } from 'react';
import authService, { AuthenticationResultStatus } from './AuthorizeService';
import { QueryParameterNames, LogoutActions, ApplicationPaths } from './ApiAuthorizationConstants';

interface Props {
    action: string;
}

interface State {
    message: string | undefined;
    isReady: boolean;
    authenticated: boolean;
}

export class Logout extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { message: undefined, isReady: false, authenticated: false };
    }

    componentDidMount() {
        const { action } = this.props;
        switch (action) {
            case LogoutActions.Logout:
                if (window.history.state?.state?.local) {
                    this.logout(this.getReturnUrl());
                } else {
                    this.setState({ isReady: true, message: 'The logout was not initiated from within the page.' });
                }
                break;
            case LogoutActions.LogoutCallback:
                this.processLogoutCallback();
                break;
            case LogoutActions.LoggedOut:
                this.setState({ isReady: true, message: 'You successfully logged out!' });
                break;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
        this.populateAuthenticationState();
    }

    render() {
        const { isReady, message } = this.state;
        if (!isReady) return <div></div>;

        if (message) {
            return <div>{this.logoutView(message)}</div>;
        }

        const { action } = this.props;
        switch (action) {
            case LogoutActions.Logout:
                return <div>Processing logout</div>;
            case LogoutActions.LogoutCallback:
                return <div>Processing logout callback</div>;
            case LogoutActions.LoggedOut:
                return <>{this.logoutView(message)}</>;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }

    private logoutView(message: string | undefined) {
        return (
            <div style={{ margin: 'auto', width: '40%', marginTop: '150px' }}>
                <p>{message}</p>
                <div className="form-group">
                    <p>
                        <a className="text-primary" href={ApplicationPaths.Login}>
                            Login again
                        </a>
                    </p>
                </div>
            </div>
        );
    }

    private async logout(returnUrl: string) {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const result = await authService.signOut({ returnUrl });
            switch (result.status) {
                case AuthenticationResultStatus.Redirect:
                    break;
                case AuthenticationResultStatus.Success:
                    this.navigateToReturnUrl(returnUrl);
                    break;
                case AuthenticationResultStatus.Fail:
                    this.setState({ message: result.message });
                    break;
                default:
                    throw new Error('Invalid authentication result status.');
            }
        } else {
            this.setState({ message: 'You successfully logged out!' });
        }
    }

    private async processLogoutCallback() {
        const result = await authService.completeSignOut(window.location.href);
        switch (result.status) {
            case AuthenticationResultStatus.Redirect:
                throw new Error('Should not redirect.');
            case AuthenticationResultStatus.Success:
                this.navigateToReturnUrl(this.getReturnUrl(result.state));
                break;
            case AuthenticationResultStatus.Fail:
                this.setState({ message: result.message });
                break;
            default:
                throw new Error('Invalid authentication result status.');
        }
    }

    private async populateAuthenticationState() {
        const authenticated = await authService.isAuthenticated();
        this.setState({ isReady: true, authenticated });
    }

    private getReturnUrl(state?: unknown): string {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            throw new Error('Invalid return url. The return url needs to have the same origin as the current page.');
        }
        return (state as any)?.returnUrl || fromQuery || `${window.location.origin}${ApplicationPaths.LoggedOut}`;
    }

    private navigateToReturnUrl(returnUrl: string) {
        window.location.replace(returnUrl);
    }
}

